import type {CodeItem} from '../../vendor/src/features/remotion-editor/editor/items/code/code-item-type';
import type {StoryTemplate} from '../../vendor/src/shared/lib/remotion/ssul-templates/story-template';
import manifest from '../../vendor/manifest.json';
import {decodeStoryDataUri} from './story-media-budget';

const MAX_CODE_BYTES = 8 * 1024 * 1024;
const MAX_TEXT = 2048;
const MAX_SEGMENTS = 1024;
const MAX_DURATION_SEC = 15 * 60;
const publicAssets = new Set(manifest.files.filter(file => file.destination.startsWith('public/'))
  .map(file => file.destination.slice('public/'.length)));

const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);
const text = (value: unknown, max = MAX_TEXT): value is string =>
  typeof value === 'string' && value.length <= max;
const number = (value: unknown, min: number, max: number): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max;
const optional = (value: unknown, check: (value: unknown) => boolean): boolean =>
  value === undefined || value === null || check(value);
const color = (value: unknown): value is string =>
  text(value, 96) && /^(?:#[\da-fA-F]{3,8}|[a-zA-Z]{1,20}|(?:rgb|rgba|hsl|hsla)\([\d.,%\s-]+\))$/.test(value);

/** The original code template has data only; never run it through a JS compiler. */
export function isCanonicalStoryCandidate(code: string): boolean {
  return code.includes('StoryTemplateComp');
}

function jsonObjectEnd(code: string, start: number): number {
  if (code[start] !== '{') return -1;
  let depth = 0, quoted = false, escaped = false;
  for (let i = start; i < code.length; i++) {
    const char = code[i];
    if (quoted) {
      if (escaped) escaped = false;
      else if (char === '\\') escaped = true;
      else if (char === '"') quoted = false;
    } else if (char === '"') quoted = true;
    else if (char === '{') depth++;
    else if (char === '}' && --depth === 0) return i + 1;
  }
  return -1;
}

function mediaUrl(value: unknown, kind: 'image' | 'video' | 'audio'): boolean {
  if (!text(value, 6 * 1024 * 1024) || !value) return false;
  if (value.startsWith('data:')) {
    const mime = kind === 'image' ? 'image/(?:png|jpeg|webp)' :
      kind === 'video' ? 'video/(?:mp4|webm)' : 'audio/(?:mpeg|mp3|wav|wave|ogg|mp4)';
    if(!new RegExp(`^data:${mime};base64,[A-Za-z0-9+/]+={0,2}$`, 'i').test(value))return false;
    if(kind!=='audio')return decodeStoryDataUri(value,kind)!==null;
    const encoded=value.slice(value.indexOf(',')+1);
    if(encoded.length%4!==0)return false;
    try{
      const raw=atob(encoded);
      if(raw.length>2*1024*1024||btoa(raw)!==encoded)return false;
      const label=value.slice(5,value.indexOf(';')).toLowerCase();
      if(label==='audio/wav'||label==='audio/wave')return raw.startsWith('RIFF')&&raw.slice(8,12)==='WAVE';
      if(label==='audio/ogg')return raw.startsWith('OggS');
      if(label==='audio/mp4')return raw.slice(4,8)==='ftyp';
      return raw.startsWith('ID3')||(raw.charCodeAt(0)===0xff&&(raw.charCodeAt(1)&0xe0)===0xe0);
    }catch{return false;}
  }
  if (value.length > 8192) return false;
  if (value.startsWith('https://')) {
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && !!url.hostname && !url.username && !url.password;
    } catch { return false; }
  }
  const path = value.startsWith('/') ? value.slice(1) : value;
  return !path.includes('..') && publicAssets.has(path);
}

function validStoryData(value: unknown): value is StoryTemplate {
  if (!record(value) || !record(value.header) || !record(value.postTitle) ||
      !record(value.meta) || !record(value.body) || !record(value.card) ||
      !Array.isArray(value.segments) || value.segments.length < 1 ||
      value.segments.length > MAX_SEGMENTS) return false;
  const h = value.header, p = value.postTitle, m = value.meta, b = value.body, c = value.card;
  if (!number(h.height, 0, 4096) || !number(h.titleSize, 1, 512) ||
      !color(h.bg) || !color(h.line) || !text(h.title) ||
      !optional(h.emoji, v => text(v, 64)) || !optional(h.accent, color) ||
      !optional(h.themeLocked, v => typeof v === 'boolean')) return false;
  for (const field of [p, m]) {
    if (!text(field.text) || !number(field.x, -4096, 4096) ||
        !number(field.y, -4096, 4096) || !number(field.size, 1, 512)) return false;
  }
  if (!number(b.startY, -4096, 4096) || !number(b.lineGap, 1, 1024) ||
      !number(b.size, 1, 512) || !number(b.cardGap, -4096, 4096) ||
      !optional(b.safeWidth, v => number(v, 1, 4096)) ||
      !optional(b.subtitleLinesPerPage, v => Number.isInteger(v) && number(v, 1, 4))) return false;
  if (!number(c.x, -4096, 4096) || !number(c.w, 1, 4096) ||
      !number(c.h, 1, 4096) || !number(c.radius, 0, 4096) ||
      !optional(c.y, v => number(v, -4096, 4096))) return false;
  if (!optional(value.background, color) ||
      !optional(value.theme, v => v === 'default' || v === 'cinematic') ||
      !optional(value.voiceOffsetSec, v => number(v, 0, MAX_DURATION_SEC))) return false;
  const sources=new Set<string>(),videoSources=new Set<string>();
  for (const segment of value.segments) {
    if (!record(segment) || !Array.isArray(segment.lines)) return false;
    const lines = segment.lines;
    if (!number(segment.t, 0, MAX_DURATION_SEC) ||
        lines.length > 32 || !lines.every(line => text(line)) ||
        !(segment.img === null ||
          mediaUrl(segment.img, /(?:\.(?:mp4|webm|mov)(?:\?|$)|^data:video\/)/i.test(String(segment.img)) ? 'video' : 'image')) ||
        !optional(segment.fx, v => ['zoomin','punch','shake','flash','pan','fade'].includes(String(v))) ||
        !optional(segment.sceneStart, v => typeof v === 'boolean') ||
        !optional(segment.videoDurationSec, v => number(v, 0, MAX_DURATION_SEC)) ||
        !optional(segment.lineTimes, v => Array.isArray(v) && v.length <= lines.length &&
          v.every(time => time === null || number(time, 0, MAX_DURATION_SEC))) ||
        !optional(segment.lineWrap, v => Array.isArray(v) && v.length <= lines.length &&
          v.every(mode => mode === null || mode === 'free')) ||
        !optional(segment.lineSetBreak, v => Array.isArray(v) && v.length <= lines.length &&
          v.every(flag => flag === null || typeof flag === 'boolean')) ||
        !optional(segment.sfx, v => mediaUrl(v, 'audio')) ||
        !optional(segment.clipInstUrl, v => mediaUrl(v, 'audio')) ||
        !optional(segment.clipAudio, v => typeof v === 'boolean')) return false;
    if(typeof segment.img==='string'){
      sources.add(segment.img);
      if(/(?:\.(?:mp4|webm|mov)(?:\?|$)|^data:video\/)/i.test(segment.img))videoSources.add(segment.img);
      if(sources.size>16||videoSources.size>4)return false;
    }
  }
  return true;
}

/** Exact serializer envelope + JSON.parse only, preserving the original object. */
export function readTrustedCanonicalStory(item: Pick<CodeItem, 'code'>): StoryTemplate | null {
  const code = item.code;
  if (!isCanonicalStoryCandidate(code) || code.length > MAX_CODE_BYTES ||
      new TextEncoder().encode(code).byteLength > MAX_CODE_BYTES) return null;
  const prefix = /^\s*const\s+data\s*=\s*/.exec(code);
  if (!prefix) return null;
  const end = jsonObjectEnd(code, prefix[0].length);
  if (end < 0 || !/^;\s*export\s+default\s+function\s+[A-Za-z_$][\w$]*\s*\(\s*\)\s*\{\s*return\s*<StoryTemplateComp\s+data=\{data\}\s*\/>;?\s*\}\s*;?\s*$/.test(code.slice(end))) return null;
  try {
    const data: unknown = JSON.parse(code.slice(prefix[0].length, end));
    return validStoryData(data) ? data : null;
  } catch { return null; }
}
